import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { useCallback, useMemo, useState } from 'react';
import { computeGroupSummary } from '../utils/computeGroupSummary';
import { resolveSessionsFullTractMetrics } from '../utils/sessionFullTractMetricsCache';
import { hasPendingSessionEventParse } from '../utils/sessionLoadingState';
import type {
  CustomTimeSegment,
  SpectralWorkspaceSession,
  SpectralWorkspaceSnapshot,
} from '../types';
import {
  buildExcelWorkbook,
  buildWorkspaceExcelExportFile,
  buildWorkspaceExportPreviews,
  downloadExcelWorkbook,
  hasHrDataRawExportData,
  WORKSPACE_HRDATA_RAW_EXPORT,
  type BuildWorkspaceExcelExportParams,
  type WorkspaceExportFileId,
} from '../export';

const yieldToMainThread = () =>
  new Promise<void>(resolve => {
    window.setTimeout(resolve, 0);
  });

export type UseWorkspaceExportParams = {
  meta: Pick<
    SpectralWorkspaceSnapshot,
    'name' | 'description' | 'author' | 'createdAt'
  >;
  sessionList: SpectralWorkspaceSession[];
  readySessions: SpectralWorkspaceSession[];
  isLoadingSessions: boolean;
  customSegments: CustomTimeSegment[];
  trendStart: string | null;
  trendEnd: string | null;
  showOverlaySessions: boolean;
  selectedOverlaySessionIds: string[];
  selectedSpectraSessionId: string | null;
  spectraStart: string | null;
  spectraEnd: string | null;
  spectraLogScale: boolean;
};

export const useWorkspaceExport = ({
  meta,
  sessionList,
  readySessions,
  isLoadingSessions,
  customSegments,
  trendStart,
  trendEnd,
  showOverlaySessions,
  selectedOverlaySessionIds,
  selectedSpectraSessionId,
  spectraStart,
  spectraEnd,
  spectraLogScale,
}: UseWorkspaceExportParams) => {
  const [opened, { open: openModal, close }] = useDisclosure(false);
  const [exportingId, setExportingId] = useState<WorkspaceExportFileId | null>(
    null
  );
  const [exportDate, setExportDate] = useState(() => new Date());

  const exportDateLabel = useMemo(
    () => exportDate.toLocaleString('it-IT'),
    [exportDate]
  );

  const excelExportPreviews = useMemo(() => {
    if (!opened) {
      return [];
    }

    return buildWorkspaceExportPreviews({
      meta,
      exportDate,
      customSegments,
    });
  }, [customSegments, exportDate, meta, opened]);

  const isPreparingExportData = useMemo(() => {
    if (!opened) {
      return false;
    }

    if (isLoadingSessions) {
      return true;
    }

    return hasPendingSessionEventParse(sessionList);
  }, [isLoadingSessions, opened, sessionList]);

  const isExportReady =
    opened && readySessions.length > 0 && !isPreparingExportData;

  const hasHrDataRawData = useMemo(
    () => hasHrDataRawExportData(readySessions),
    [readySessions]
  );

  const buildExportParams = useCallback((): BuildWorkspaceExcelExportParams => {
    const metrics = resolveSessionsFullTractMetrics(readySessions).metrics;

    return {
      meta,
      summary: computeGroupSummary(sessionList, metrics),
      exportDateLabel,
      exportDate,
      sessions: sessionList,
      metrics,
      readySessions,
      customSegments,
      trendStart,
      trendEnd,
      showOverlaySessions,
      selectedOverlaySessionIds,
      selectedSpectraSessionId,
      spectraStart,
      spectraEnd,
      spectraLogScale,
    };
  }, [
    customSegments,
    exportDate,
    exportDateLabel,
    meta,
    readySessions,
    selectedOverlaySessionIds,
    selectedSpectraSessionId,
    sessionList,
    showOverlaySessions,
    spectraEnd,
    spectraLogScale,
    spectraStart,
    trendEnd,
    trendStart,
  ]);

  const handleExportSingle = useCallback(
    async (exportId: WorkspaceExportFileId) => {
      if (isPreparingExportData) {
        return;
      }

      const isHrDataRaw = exportId === WORKSPACE_HRDATA_RAW_EXPORT.id;
      if (isHrDataRaw ? !hasHrDataRawData : !isExportReady) {
        return;
      }

      const preview = excelExportPreviews.find(entry => entry.id === exportId);

      if (!preview) {
        return;
      }

      setExportingId(exportId);

      try {
        await yieldToMainThread();

        const entry = buildWorkspaceExcelExportFile(
          exportId,
          buildExportParams()
        );
        const sheetsWithData = entry.sheets.filter(
          sheet => sheet.rows.length > 0
        );

        if (sheetsWithData.length === 0) {
          notifications.show({
            color: 'yellow',
            title: 'Attenzione',
            message: 'Nessun dato disponibile per questo file.',
          });
          return;
        }

        const workbook = buildExcelWorkbook(sheetsWithData);
        downloadExcelWorkbook(workbook, entry.filename);

        notifications.show({
          color: 'green',
          title: 'Esportazione completata',
          message: `Scaricato ${entry.filename} (${sheetsWithData.length} fogli).`,
        });
      } catch (error) {
        notifications.show({
          color: 'red',
          title: 'Errore export',
          message:
            error instanceof Error
              ? error.message
              : 'Impossibile generare il file Excel.',
        });
      } finally {
        setExportingId(null);
      }
    },
    [
      buildExportParams,
      excelExportPreviews,
      hasHrDataRawData,
      isExportReady,
      isPreparingExportData,
    ]
  );

  const open = useCallback(() => {
    setExportDate(new Date());
    openModal();
  }, [openModal]);

  return {
    opened,
    open,
    close,
    exportingId,
    isExporting: exportingId !== null,
    isPreparingExportData,
    isExportReady,
    handleExportSingle,
    excelExports: excelExportPreviews.map(preview => ({
      exportId: preview.id,
      label: preview.label,
      filename: preview.filename,
      sheetNames: preview.sheetNames,
      sheetCount: preview.sheetCount,
      hasData:
        preview.id === WORKSPACE_HRDATA_RAW_EXPORT.id
          ? !isPreparingExportData && hasHrDataRawData
          : isExportReady,
    })),
  };
};
